import React from 'react'
import LRU from 'lru-cache'
import { stringify } from 'query-string'

import axios from 'axios'
import config from '../config'
import AuthRetrier from '../api/authRetrier'

import ChassisDocumentsAPI from '../api/chassis/documents'
import ChassisAPI from '../api/chassis'
import ControlAPI from '../api/control'
import AutocompleteEmailsAPI from '../api/utils/autocomplete-emails'
import ConstructorAPI from '../api/constructor'
import ConstructorControlAPI from '../api/constructor-control'
import OperatorAPI from '../api/operator'
import ContractorAPI from '../api/contractor'
import MembersAPI from '../api/members'
import MemberControlAPI from '../api/member-control'
import PermissionsAPI from '../api/permissions'
import VehicleTypeAPI from '../api/vehicle-type'
import BulkUploadAPI from '../api/bulk-upload'
import CalendarAPI from '../api/calendar'
import QuickFilterAPI from '../api/quick-filter'
import PVAdmissionTemplateAPI from '../api/pv-admission-template'
import Storages from './user/storages'

const cacheOptions = {
  max: 100,
  maxAge: 1000 * 60, // 1 minute in miliseconds
}

const cache = new LRU(cacheOptions)

const DataApiContext = React.createContext()
const worldHTTPClient = axios.create()

let httpClient = null

const getAuthToken = () => {
  const tokens = Storages.tokens.inflate()
  if (tokens) {
    const { token } = tokens
    return token
  }
  return null
}

function getHTTPClient(baseURL) {
  if (baseURL === undefined) {
    baseURL = `${config.apiBaseUrl}/su`
  }

  if (!httpClient) {
    console.log('Creating http client')
    const headers = {
      'Content-Type': 'application/json',
    }
    const token = getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    httpClient = axios.create({
      baseURL,
      headers,
    })
    AuthRetrier(httpClient)
  }
  return httpClient
}

const resetHTTPClient = () => {
  console.log('Reseting http client')
  cache.reset()
  httpClient = null
}

function prepareUrlForQuery(resource, range, filters, sort, merge) {
  const { page, limit } = range || {}
  let query = {
    ...merge,
  }
  if (filters) {
    // eslint-disable-next-line
    for (const [key, value] of Object.entries(filters)) {
      query[`filters[${key}]`] = value
    }
  }
  if (page) {
    query = { ...query, 'range[page]': page }
  }
  if (limit) {
    query = { ...query, 'range[limit]': limit }
  }
  if (sort) {
    const { field, direction } = sort
    const parts = field.split('.')
    if (parts.length > 0) {
      const sortKey = parts.reduce((key, part) => `${key}[${part}]`, 'sort')
      query = {
        ...query,
        [sortKey]: direction,
      }
    }
  }

  const stringifiedQuery = stringify(query)
  return [resource, stringifiedQuery].filter((el) => el).join('?')
}

function ApiDataProvider({ children }) {
  const dataProvider = {
    get: (resource, params) => {
      let url = `${resource}`
      if (params.query) {
        url += `?${stringify(params.query)}`
      }
      const cachedData = cache.get(url)
      if (cachedData) {
        return Promise.resolve(cachedData)
      }
      return getHTTPClient()({ url, method: 'GET' }).then((response) => {
        cache.set(url, response)
        return response
      })
    },
    post: (resource, params) => {
      cache.reset()
      const url = `${resource}`
      return getHTTPClient()({
        url,
        method: 'POST',
        data: JSON.stringify(params.data),
      })
    },
    put: (resource, params) => {
      cache.reset()
      const url = `${resource}`
      return getHTTPClient()({
        url,
        method: 'PUT',
        data: JSON.stringify(params.data),
      })
    },
    patch: (resource, params) => {
      cache.reset()
      const url = `${resource}`
      return getHTTPClient()({
        url,
        method: 'PATCH',
        data: JSON.stringify(params.data),
      })
    },
    getList: (resource, params, merge, useCache = true) => {
      const url = prepareUrlForQuery(
        resource,
        params.range,
        params.filters,
        params.sort,
        merge
      )
      const cachedData = cache.get(url)
      if (cachedData && useCache) {
        return Promise.resolve(cachedData)
      }
      return getHTTPClient()({ url, method: 'GET' }).then((response) => {
        cache.set(url, response)
        return response
      })
    },
    getOne: (resource, params) => {
      const url = `${resource}/${params.id}`
      const cachedData = cache.get(url)
      if (cachedData) {
        return Promise.resolve(cachedData)
      }
      return getHTTPClient()({ url, method: 'GET' }).then((response) => {
        cache.set(url, response)
        return response
      })
    },
    update: (resource, params) => {
      cache.reset()
      return getHTTPClient()({
        url: `${resource}/${params.id}/edit`,
        method: 'PUT',
        data: JSON.stringify(params.data),
      })
    },
    updateMany: (resource, params) =>
      Promise.all(
        params.ids.map((id) =>
          httpClient({
            method: 'PUT',
            url: `${resource}/${id}`,
            body: JSON.stringify(params.data),
          })
        )
      ),
    create: (resource, params) => {
      cache.reset()
      return getHTTPClient()({
        url: `${resource}`,
        method: 'POST',
        data: JSON.stringify(params.data),
      })
    },
    delete: (resource, params) => {
      cache.reset()
      return getHTTPClient()({
        url: `${resource}/${params.id}`,
        method: 'DELETE',
      })
    },
    deleteMany: (resource, params) =>
      Promise.all(
        params.ids.map((id) =>
          getHTTPClient()({ url: `/${resource}/${id}`, method: 'DELETE' })
        )
      ),
    deleteBulk: (resource, body) => {
      cache.reset()
      return getHTTPClient()({
        url: resource,
        data: JSON.stringify(body),
        method: 'DELETE',
      })
    },
    upload: (params) => {
      const { file } = params
      return getHTTPClient()({
        url: '/upload',
        method: 'POST',
      }).then((response) => {
        const headersUpload = response.data.uploadHeaders || {}
        headersUpload['Content-Type'] = file.type
        return worldHTTPClient({
          url: response.data.uploadUrl,
          method: response.data.uploadHttpMethod,
          headers: headersUpload,
          data: file,
        }).then(() => Promise.resolve({ photoUrl: response.data.photoUrl }))
      })
    },
    prepareUrlForQuery,
    generateExportUrl: (resource, params, type) => {
      const url = prepareUrlForQuery(
        `${resource}/${type}`,
        params.range,
        params.filters,
        params.sort,
        {
          token: getAuthToken(),
        }
      )

      return `${config.apiBaseUrl}/exports/${url}`
    },
    generatePVAdmissionExportUrl: (controlId, asDocx) => {
      if (typeof controlId !== 'number') {
        return null
      }
      return `${config.apiBaseUrl}/exports/control/${controlId}/pv-admission?asDocx=${asDocx}`
    },

    // Async export methods
    startAsyncExport: (type, params) => {
      const filtersObj = {}
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          filtersObj[key] = value
        })
      }

      const payload = {
        type,
        filters: filtersObj,
      }

      // Only include sort if it's actually set, otherwise let backend use default
      if (params.sort && params.sort.field) {
        payload.sort = {
          [params.sort.field]: params.sort.direction,
        }
      }

      return getHTTPClient()({
        url: `${config.apiBaseUrl}/exports/async/start`,
        method: 'POST',
        data: JSON.stringify(payload),
      })
    },

    getAsyncExportStatus: (batchId) => {
      return getHTTPClient()({
        url: `${config.apiBaseUrl}/exports/async/status/${batchId}`,
        method: 'GET',
      })
    },

    getMyExports: () => {
      return getHTTPClient()({
        url: `${config.apiBaseUrl}/exports/async/my-exports`,
        method: 'GET',
      })
    },
    fetchPVAdmissionByParkNumber: (parkNumber, asDocx) => {
      if (!parkNumber || parkNumber.length === 0) {
        return Promise.reject(new Error('Park number is required'))
      }

      // Create a dedicated axios instance for this request
      const headers = {
        'Content-Type': 'application/json',
      }
      const token = getAuthToken()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const exportClient = axios.create({
        baseURL: config.apiBaseUrl,
        headers,
      })
      AuthRetrier(exportClient)

      return exportClient({
        url: `/exports/chassis/${parkNumber}/pv-admission?asDocx=${asDocx}`,
        method: 'GET',
        responseType: 'blob',
      })
    },
    checkPVAdmissionAvailability: (controlId) => {
      if (typeof controlId !== 'number') {
        return Promise.reject(new Error('Invalid control ID'))
      }
      return httpClient.get(
        `${config.apiBaseUrl}/exports/control/${controlId}/pv-admission/availability`
      )
    },

    // New API context structure
    chassis: {
      getOverrideValidationConstraints: (ids) =>
        ChassisAPI.getOverrideValidationConstraints(ids, getHTTPClient()),
      overrideValidation: (ids) =>
        ChassisAPI.overrideValidation(ids, getHTTPClient()),
      validateBulkUpload: (chassisData) =>
        ChassisAPI.validateBulkUpload(chassisData, getHTTPClient()),
      startBulkUpload: () => ChassisAPI.startBulkUpload(getHTTPClient()),
      performBulkUpload: (uploadDetails, csvData) =>
        ChassisAPI.performBulkUpload(uploadDetails, csvData, worldHTTPClient),
      recordImportCorrections: (corrections) =>
        ChassisAPI.recordImportCorrections(corrections, getHTTPClient()),
      documents: {
        get: (chassisId) => ChassisDocumentsAPI.get(chassisId, getHTTPClient()),
        upload: (response, file) =>
          ChassisDocumentsAPI.upload(response, file, worldHTTPClient),
        requestUpload: (chassisId) =>
          ChassisDocumentsAPI.requestUpload(chassisId, getHTTPClient()),
        post: (chassisId, type, url, originalName) =>
          ChassisDocumentsAPI.post(
            chassisId,
            type,
            url,
            originalName,
            getHTTPClient()
          ),
        deleteDocuments: (chassisId, types) =>
          ChassisDocumentsAPI.deleteDocuments(
            chassisId,
            types,
            getHTTPClient()
          ),
        downloadDocuments: (chassisId, types) =>
          ChassisDocumentsAPI.downloadDocuments(
            chassisId,
            types,
            getHTTPClient()
          ),
      },
      deleteBulk: (chassisIds) =>
        ChassisAPI.deleteBulk(chassisIds, getHTTPClient()),
      uploadPVAdmissionPreview: (file) => {
        return getHTTPClient()({
          url: '/upload/pv-admission-preview',
          method: 'POST',
        }).then((response) => {
          const headersUpload = response.data.uploadHeaders || {}
          headersUpload['Content-Type'] = file.type
          return worldHTTPClient({
            url: response.data.uploadUrl,
            method: response.data.uploadHttpMethod,
            headers: headersUpload,
            data: file,
          }).then(() => Promise.resolve({ fileUrl: response.data.url }))
        })
      },
      deletePVAdmissionPreview: (fileUrl) => {
        return getHTTPClient()({
          url: `/upload/pv-admission-preview`,
          method: 'DELETE',
          data: JSON.stringify({ url: fileUrl }),
        })
      },
      getPVAdmissionManagement: (documentType) => {
        const type = documentType || 'acquisition'
        return getHTTPClient()({
          url: `/chassis/pv-admission-management?documentType=${type}`,
          method: 'GET',
        })
      },
      uploadPVAdmissionManagement: (file, documentFormat, documentType) => {
        const type = documentType || 'acquisition'
        return getHTTPClient()({
          url: '/upload/pv-admission-management',
          method: 'POST',
          data: JSON.stringify({
            documentFormat: documentFormat,
            documentType: type,
          }),
        }).then((response) => {
          const headersUpload = response.data.uploadHeaders || {}
          headersUpload['Content-Type'] = file.type
          return worldHTTPClient({
            url: response.data.uploadUrl,
            method: response.data.uploadHttpMethod,
            headers: headersUpload,
            data: file,
          }).then(() => Promise.resolve({ fileUrl: response.data.url }))
        })
      },
    },

    pvAdmissionTemplates: {
      getAll: () => PVAdmissionTemplateAPI.getAll(getHTTPClient()),
      create: (name) => PVAdmissionTemplateAPI.create(name, getHTTPClient()),
      rename: (templateId, name) =>
        PVAdmissionTemplateAPI.rename(templateId, name, getHTTPClient()),
      remove: (templateId) =>
        PVAdmissionTemplateAPI.remove(templateId, getHTTPClient()),
      uploadDocument: (templateId, file) =>
        PVAdmissionTemplateAPI.uploadDocument(
          templateId,
          file,
          getHTTPClient(),
          worldHTTPClient
        ),
      acceptDocumentWarnings: (templateId, stagingKey) =>
        PVAdmissionTemplateAPI.acceptDocumentWarnings(
          templateId,
          stagingKey,
          getHTTPClient()
        ),
    },

    control: {
      getDeleteWaitingResultConstraints: (ids) =>
        ControlAPI.getDeleteWaitingResultConstraints(ids, getHTTPClient()),
      deleteWaitingResult: (ids) =>
        ControlAPI.deleteWaitingResult(ids, getHTTPClient()),
      emailResult: (controlId, emails, sendTo) =>
        ControlAPI.emailResult(controlId, emails, sendTo, getHTTPClient()),
      emailResultByList: (controlId, emailLists) =>
        ControlAPI.emailResultByList(controlId, emailLists, getHTTPClient()),
    },

    constructorControl: {
      getDeleteWaitingResultConstraints: (controlIds, chassisIds) =>
        ConstructorControlAPI.getDeleteWaitingResultConstraints(
          controlIds,
          chassisIds,
          getHTTPClient()
        ),
      deleteWaitingResult: (controlIds, chassisIds) =>
        ConstructorControlAPI.deleteWaitingResult(
          controlIds,
          chassisIds,
          getHTTPClient()
        ),
      emailResult: (controlId, emails, sendTo) =>
        ConstructorControlAPI.emailResult(
          controlId,
          emails,
          sendTo,
          getHTTPClient()
        ),
      emailResultByList: (controlId, emailLists) =>
        ConstructorControlAPI.emailResultByList(
          controlId,
          emailLists,
          getHTTPClient()
        ),
      getItems: (controlId) =>
        ConstructorControlAPI.getItems(controlId, getHTTPClient()),
      getDocuments: (controlId) =>
        ConstructorControlAPI.getDocuments(controlId, getHTTPClient()),
      getOtherControls: (controlId) =>
        ConstructorControlAPI.getOtherControls(controlId, getHTTPClient()),
    },

    memberControl: {
      getDeleteWaitingResultConstraints: (controlIds, chassisIds) =>
        MemberControlAPI.getDeleteWaitingResultConstraints(
          controlIds,
          chassisIds,
          getHTTPClient()
        ),
      deleteWaitingResult: (controlIds, chassisIds) =>
        MemberControlAPI.deleteWaitingResult(
          controlIds,
          chassisIds,
          getHTTPClient()
        ),
      emailResult: (controlId, emails, sendTo) =>
        MemberControlAPI.emailResult(
          controlId,
          emails,
          sendTo,
          getHTTPClient()
        ),
      emailResultByList: (controlId, emailLists) =>
        MemberControlAPI.emailResultByList(
          controlId,
          emailLists,
          getHTTPClient()
        ),
      getItems: (controlId) =>
        MemberControlAPI.getItems(controlId, getHTTPClient()),
      getDocuments: (controlId) =>
        MemberControlAPI.getDocuments(controlId, getHTTPClient()),
      getOtherControls: (controlId) =>
        MemberControlAPI.getOtherControls(controlId, getHTTPClient()),
    },

    utils: {
      autocompleteEmails: () =>
        AutocompleteEmailsAPI.autocompleteEmails(getHTTPClient()),
      autocompleteEmailLists: (query, control) =>
        AutocompleteEmailsAPI.autocompleteEmailLists(
          query,
          getHTTPClient(),
          control
        ),
    },

    cache,

    constructor: {
      getList: (resource, params, useCache) =>
        ConstructorAPI.getList(resource, params, getHTTPClient(), useCache),
      post: (name, address, siret, vehicleTypeIds, controlLocationIds) =>
        ConstructorAPI.post(
          name,
          address,
          siret,
          vehicleTypeIds,
          controlLocationIds,
          getHTTPClient()
        ),
      put: (
        constructorId,
        name,
        address,
        siret,
        vehicleTypeIds,
        controlLocationIds
      ) =>
        ConstructorAPI.put(
          constructorId,
          name,
          address,
          siret,
          vehicleTypeIds,
          controlLocationIds,
          getHTTPClient()
        ),
    },
    operator: {
      getList: (resource, params, useCache) =>
        OperatorAPI.getList(resource, params, getHTTPClient(), useCache),
      post: (name, depots, memberIds) =>
        OperatorAPI.post(name, depots, memberIds, getHTTPClient()),
      put: (operatorId, name, depots, memberIds) =>
        OperatorAPI.put(operatorId, name, depots, memberIds, getHTTPClient()),
    },
    contractor: {
      getList: (resource, params, useCache) =>
        ContractorAPI.getList(resource, params, getHTTPClient(), useCache),
      post: (firstName, lastName, email) =>
        ContractorAPI.post(firstName, lastName, email, getHTTPClient()),
      put: (contractorId, firstName, lastName) =>
        ContractorAPI.put(contractorId, firstName, lastName, getHTTPClient()),
    },
    member: {
      getList: (resource, params, useCache) =>
        MembersAPI.getList(resource, params, getHTTPClient(), useCache),
      post: (name, chassisIds) =>
        MembersAPI.post(name, chassisIds, getHTTPClient()),
      put: (memberId, name, chassisIds) =>
        MembersAPI.put(memberId, name, chassisIds, getHTTPClient()),
      resetCache: () => {
        MembersAPI.cache.reset()
      },
    },
    permissions: {
      get: () => PermissionsAPI.get(getHTTPClient()),
      post: (role, permission) =>
        PermissionsAPI.post(role, permission, getHTTPClient()),
      deletePermission: (role, permission) =>
        PermissionsAPI.deletePermission(role, permission, getHTTPClient()),
    },
    vehicleType: {
      associateDocumentTypes: (vehicleTypeId, body) =>
        VehicleTypeAPI.associateDocumentTypes(
          vehicleTypeId,
          body,
          getHTTPClient()
        ),
      startItemsBulkUpload: () =>
        BulkUploadAPI.startVehicleTypeItems(getHTTPClient()),
      performBulkUpload: (uploadDetails, file) =>
        BulkUploadAPI.performUpload(uploadDetails, file, worldHTTPClient),
    },

    calendar: {
      exportTemplate: (constructorId) =>
        CalendarAPI.exportTemplate(constructorId, getHTTPClient()),
      validateImport: (rows, constructorId) =>
        CalendarAPI.validateImport(rows, constructorId, getHTTPClient()),
      confirmImport: (rows) => CalendarAPI.confirmImport(rows, getHTTPClient()),
    },

    quickFilter: {
      list: (pageType) => QuickFilterAPI.list(pageType, getHTTPClient()),
      create: (payload) => {
        cache.reset()
        return QuickFilterAPI.create(payload, getHTTPClient())
      },
      update: (id, payload) => {
        cache.reset()
        return QuickFilterAPI.update(id, payload, getHTTPClient())
      },
      remove: (id) => {
        cache.reset()
        return QuickFilterAPI.remove(id, getHTTPClient())
      },
      setDefault: (id) => {
        cache.reset()
        return QuickFilterAPI.setDefault(id, getHTTPClient())
      },
      clearDefault: (pageType) => {
        cache.reset()
        return QuickFilterAPI.clearDefault(pageType, getHTTPClient())
      },
    },
  }

  return (
    <DataApiContext.Provider value={{ dataProvider }}>
      {children}
    </DataApiContext.Provider>
  )
}

function useDataApi() {
  const context = React.useContext(DataApiContext)
  if (context === undefined) {
    throw new Error('useDataState must be used within a ApiDataProvider')
  }
  return context
}

export { ApiDataProvider, useDataApi, resetHTTPClient }
